import type {
  AmplifyRequest,
  AmplifyResponse,
  GenerateRequest,
  GenerationCard,
  GenerationResult,
  SuggestRequest,
  SuggestResponse,
  Whale,
} from '@gilding/shared';
import { anonId } from './../lib/anon.js';

// Dev: use Vite's /api proxy (rewrites to localhost:8787).
// Prod: set VITE_API_BASE to the full Worker URL at build time, e.g.
//   VITE_API_BASE=https://gilding-worker.<your-subdomain>.workers.dev npm run build
const BASE = (import.meta.env.VITE_API_BASE as string | undefined) || '/api';

const TOKEN_KEY = 'gilding_session';

/**
 * Route a whale image through the worker's caching proxy (GET /whales/img).
 * The indexer's Cloudinary/IPFS URLs hard rate-limit a large pod loading at
 * once; the proxy fetches each image once and serves it from R2/our edge, so
 * grids load reliably. Display only — generation still uses the raw imageUrl.
 * Never double-proxies our own image URLs.
 */
export function whaleImgUrl(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  if (url.includes('/whales/img') || url.includes('/images/')) return url;
  return `${BASE}/whales/img?u=${encodeURIComponent(url)}`;
}

/**
 * Auth token storage. We persist the token in localStorage and ship it as
 * Authorization: Bearer on every request — cookies don't survive the
 * cross-origin hop from .pages.dev to .workers.dev on mobile in-app browsers
 * (MetaMask app, iOS Safari with ITP, etc).
 */
export const session = {
  read(): string | null {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(TOKEN_KEY);
  },
  write(token: string): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(TOKEN_KEY, token);
  },
  clear(): void {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(TOKEN_KEY);
  },
};

interface ApiError {
  error: string;
  message: string;
  detail?: unknown;
}
async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = session.read();
  const authHeader: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...authHeader,
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  const contentType = res.headers.get('content-type') ?? '';
  // Defensive: if the response isn't JSON (e.g. Pages served the SPA fallback
  // because /api/* wasn't routed), surface a clean error instead of letting
  // res.json() throw a cryptic SyntaxError. WebKit's JSON.parse message is
  // "The string did not match the expected pattern" which gives users nothing.
  if (!contentType.includes('application/json')) {
    const preview = await res.text().catch(() => '');
    const err = new Error(
      `Expected JSON from ${path} but got ${contentType || 'unknown content-type'} (status ${res.status}). Worker URL may be misconfigured.`,
    ) as Error & { code?: string; detail?: unknown };
    err.code = 'bad_response';
    err.detail = { status: res.status, contentType, preview: preview.slice(0, 200) };
    throw err;
  }
  if (!res.ok) {
    let body: ApiError = { error: 'unknown', message: 'unknown' };
    try {
      body = (await res.json()) as ApiError;
    } catch {
      /* keep default */
    }
    const err = new Error(body.message) as Error & { code?: string; detail?: unknown };
    err.code = body.error;
    err.detail = body.detail;
    throw err;
  }
  return (await res.json()) as T;
}

export const api = {
  health: () => jsonFetch<{ ok: boolean }>('/health'),
  nonce: () => jsonFetch<{ nonce: string }>('/auth/nonce'),
  verify: async (message: string, signature: string) => {
    const result = await jsonFetch<{
      ok: boolean;
      address: string;
      token: string;
      holder: boolean;
    }>('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ message, signature }),
    });
    if (result.token) session.write(result.token);
    return result;
  },
  me: () =>
    jsonFetch<{
      authenticated: boolean;
      address?: string;
      credits?: number;
      dailySpent?: number;
      dailyCap?: number;
      tier?: 'free' | 'pro' | 'admin';
      /** Whacky Whales holder — gates the full site (early access). */
      holder?: boolean;
    }>('/auth/me'),
  /** Re-run the on-chain holder check for the signed-in wallet (Early Access page). */
  reverify: () =>
    jsonFetch<{ holder: boolean; granted: number; credits?: number }>('/auth/reverify', {
      method: 'POST',
    }),
  logout: async () => {
    const result = await jsonFetch<{ ok: boolean }>('/auth/logout', { method: 'POST' });
    session.clear();
    return result;
  },
  amplify: (req: AmplifyRequest) =>
    jsonFetch<AmplifyResponse>('/amplify', { method: 'POST', body: JSON.stringify(req) }),
  generate: (req: GenerateRequest) =>
    jsonFetch<GenerationResult>('/generate', { method: 'POST', body: JSON.stringify(req) }),
  // Resilient ("background") gild. Returns immediately with the id + brief; the
  // image render finishes server-side even if the app is backgrounded. Poll
  // generationStatus(id) and load the result when it reads 'done'.
  startGenerate: (req: GenerateRequest) =>
    jsonFetch<{ id: string; status: 'processing'; amplified: AmplifyResponse }>('/generate/start', {
      method: 'POST',
      body: JSON.stringify(req),
    }),
  generationStatus: (id: string) =>
    jsonFetch<{ state: 'processing' | 'failed' | 'done' | 'unknown'; message?: string | null }>(
      `/generations/${id}/status`,
    ),
  // Anonymous, no-wallet free taste — one image, metered server-side by an
  // anon device id (+ IP backstop + global ceiling). Returns the result inline;
  // nothing is saved to a gallery.
  generateFree: (req: {
    sketch: string;
    stylePreset: string;
    aspectRatio: string;
    /** Optional whale (/whales/lookup) or uploaded (/uploads/reference-anon) image. */
    referenceImageUrl?: string | null;
  }) =>
    jsonFetch<{
      id: string;
      createdAt: string;
      request: {
        sketch: string;
        stylePreset: string;
        aspectRatio: string;
        referenceImageUrl?: string | null;
      };
      amplified: { amplified: string; negative: string };
      images: { url: string; model: string; idx: number }[];
      free: true;
    }>('/generate/free', {
      method: 'POST',
      headers: { 'X-Anon-Id': anonId() },
      body: JSON.stringify(req),
    }),
  /** Public: look up any Whacky Whale by number → its image/traits (free-gild reference). */
  whalesLookup: (tokenId: number) =>
    jsonFetch<{ whale: Whale; cached: boolean }>(`/whales/lookup?tokenId=${tokenId}`),
  /** Public: upload an image (whale/NFT/character) as a free-gild reference — no wallet. */
  uploadReferenceAnon: async (file: File): Promise<{ url: string; key: string; sizeBytes: number }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/uploads/reference-anon`, { method: 'POST', body: form });
    if (!res.ok) {
      const detail: unknown = await res.json().catch(() => null);
      const message =
        detail && typeof detail === 'object' && 'message' in detail
          ? (detail as { message: string }).message
          : `upload_failed_${res.status}`;
      const err = new Error(message) as Error & { code?: string };
      err.code = 'upload_failed';
      throw err;
    }
    return res.json() as Promise<{ url: string; key: string; sizeBytes: number }>;
  },
  // Claim a previously-run anonymous free gilding into the signed-in wallet's
  // gallery. Called once on first connect; idempotent server-side.
  claimFreeGild: (id: string) =>
    jsonFetch<{ ok: boolean; id?: string; alreadyClaimed?: boolean }>('/generate/claim-free', {
      method: 'POST',
      body: JSON.stringify({ id }),
    }),
  balance: () => jsonFetch<{ credits: number; dailySpent: number }>('/credits/balance'),
  metric: (event: string, durationMs = 0) => jsonFetch('/events', { method: 'POST', body: JSON.stringify({ event, durationMs }) }),
  productHealth: () => jsonFetch<{ metrics: { days: { date: string; events: Record<string, { count: number; totalMs: number }> }[] }; reports: { id: string; index: number; issue: string; note: string; reference: string | null; output: { url: string }; tokenId: number | null; at: string }[]; moreReports: boolean }>('/activity/health'),
  reportTrait: (id: string, index: number, issue: string, note: string) => jsonFetch<{ ok: boolean }>(`/generations/${id}/trait-report`, { method: 'POST', body: JSON.stringify({ index, issue, note }) }),
  activity: () => jsonFetch<{ jobs: { id: string; at: number; label: string; state: string; message: string | null }[]; purchases: { signature: string; credits: number; packId: string; network: string; at: number }[] }>('/activity'),
  packs: () =>
    jsonFetch<{
      packs: { id: string; label: string; priceUsd: number; credits: number; perks: string[] }[];
      payment: {
        treasury: string | null;
        chainId: number;
        chainIdHex: string;
        chainName: string;
        usdcAddress: string;
        usdcDecimals: number;
        configured: boolean;
      };
    }>('/credits/packs'),
  confirmPayment: (packId: string, txHash: string) =>
    jsonFetch<{
      ok?: boolean;
      pending?: boolean;
      alreadyCredited?: boolean;
      credited?: number;
      balance?: number;
    }>('/credits/confirm', {
      method: 'POST',
      body: JSON.stringify({ packId, txHash }),
    }),
  feed: (limit = 20, offset = 0) =>
    jsonFetch<{ items: unknown[]; total: number }>(`/feed?limit=${limit}&offset=${offset}`),
  /** Photoreal hero corridor renders, in scroll order (empty until generated). */
  hero: () => jsonFetch<{ count: number; ready: number[]; urls: string[] }>(`/hero`),
  /** The curated trio of gildings the landing animates as living sculpts. */
  showcase: () =>
    jsonFetch<{
      items: {
        slug: string;
        id: string;
        cover: string;
        coverIndex: number;
        aspectRatio: string | null;
        depth: boolean;
      }[];
    }>(`/showcase`),
  leaderboard: () =>
    jsonFetch<{
      weekStart: string;
      entries: {
        address: string;
        score: number;
        displayName: string | null;
        topGenerationId: string | null;
        image: string | null;
        stylePreset: string | null;
      }[];
    }>('/leaderboard'),
  whales: (fresh = false, cursor?: string, progressive = false) =>
    jsonFetch<{ whales: Whale[]; cached: boolean; nextCursor?: string | null }>(`/whales?fresh=${fresh ? '1' : '0'}${progressive ? '&progressive=1' : ''}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`),

  // Shareable "drop" — locked public remix page for one published piece.
  campaign: (id: string) =>
    jsonFetch<
      | { active: false }
      | {
          active: true;
          id: string;
          thumbnail: string;
          stylePreset: string;
          aspectRatio: '1:1' | '3:2' | '2:3' | '16:9' | '9:16';
          maxPerWallet: number;
          remainingForWallet: number | null;
          walletAddress: string | null;
        }
    >(`/campaign/${id}`),

  // Generations — owner gallery, visibility-gated read, publish/unpublish.
  myGenerations: (limit = 50, offset = 0) =>
    // `onFeed` (per item + the total) is authoritative feed membership, so the
    // gallery's "on the feed" tally never drifts from a stale visibility flag.
    jsonFetch<{
      items: (GenerationResult & { upvotes: number; remixes: number; onFeed: boolean })[];
      total: number;
      onFeed: number;
    }>(`/generations/mine?limit=${limit}&offset=${offset}`),
  generation: (id: string) =>
    jsonFetch<
      | { kind: 'full'; result: GenerationResult; upvotes: number; remixes: number }
      | { kind: 'card'; card: GenerationCard }
    >(`/generations/${id}`),
  publish: (id: string, coverIndex = 0) =>
    jsonFetch<{ ok: boolean; visibility: 'public' }>(`/generations/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify({ coverIndex }),
    }),
  unpublish: (id: string) =>
    jsonFetch<{ ok: boolean; visibility: 'private' }>(`/generations/${id}/unpublish`, {
      method: 'POST',
    }),
  setCover: (id: string, coverIndex: number) =>
    jsonFetch<{ ok: boolean; coverIndex: number }>(`/generations/${id}/cover`, {
      method: 'POST',
      body: JSON.stringify({ coverIndex }),
    }),
  deleteGeneration: (id: string) =>
    jsonFetch<{ ok: boolean }>(`/generations/${id}/delete`, {
      method: 'POST',
    }),
  // Spend one leaf to unlock the HD, watermark-free motion clip (rendered client-
  // side). Returns the remaining balance so the leaf badge refreshes.
  // Unlock the HD clip for one image of a generation. Charged once per image;
  // a re-download of an already-unlocked image returns charged:0 (no re-charge).
  motionClip: (id: string, imageIndex = 0) =>
    jsonFetch<{ ok: boolean; credits: number | null; charged: number; alreadyUnlocked?: boolean }>(
      `/generations/${id}/motion-clip`,
      { method: 'POST', body: JSON.stringify({ imageIndex }) },
    ),
  /** Which image indices of a generation already have HD unlocked (owner-only). */
  clipEntitlement: (id: string) =>
    jsonFetch<{ unlocked: number[] }>(`/generations/${id}/clip-entitlement`),
  upvote: (id: string) =>
    jsonFetch<{ ok: boolean; upvotes: number; voted: boolean }>('/feed/upvote', {
      method: 'POST',
      body: JSON.stringify({ generationId: id }),
    }),

  // Fix mode — check entitlement and fire a targeted edit.
  fixStatus: (id: string) =>
    jsonFetch<{ freeFixAvailable: boolean; fixCreditCost: number; unlimitedFixes?: boolean }>(
      `/generations/${id}/fix-status`,
    ),

  // Surprise-me — Claude generates a fresh scene + matching preset
  // based on the optional character reference.
  suggest: (req: SuggestRequest) =>
    jsonFetch<SuggestResponse>('/suggest', { method: 'POST', body: JSON.stringify(req) }),
  // Anonymous "Surprise me" for the no-wallet free generator — same roll, no
  // character, metered server-side by the anon device id.
  suggestFree: () =>
    jsonFetch<SuggestResponse>('/suggest/free', {
      method: 'POST',
      headers: { 'X-Anon-Id': anonId() },
      body: JSON.stringify({}),
    }),

  uploadReference: async (file: File): Promise<{ url: string; key: string; sizeBytes: number }> => {
    const token = session.read();
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/uploads/reference`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) {
      const ct = res.headers.get('content-type') ?? '';
      let detail: unknown = null;
      if (ct.includes('application/json')) {
        detail = await res.json().catch(() => null);
      }
      const message =
        detail && typeof detail === 'object' && 'message' in detail
          ? (detail as { message: string }).message
          : `upload_failed_${res.status}`;
      const err = new Error(message) as Error & { code?: string; detail?: unknown };
      err.code = 'upload_failed';
      err.detail = detail;
      throw err;
    }
    return res.json() as Promise<{ url: string; key: string; sizeBytes: number }>;
  },
};

export type Api = typeof api;
